import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import GoogleSignInButton from './oauth/GoogleSignInButton';
import { getApiUrl } from '../config/api';
import { setCsrfToken } from '../utils/api';
import './Auth.css';

const Auth = ({ onAuthSuccess }) => {
  const [mode, setMode] = useState('login'); // 'login', 'signup', or 'forgot-password'
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });

  const validatePasswordStrength = (password) => {
    return {
      length: password.length >= 12,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*()_+\-=\x5B\]{};':"\\|,.<>/?]/.test(password)
    };
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    setError('');

    // Validate password strength in real-time for signup mode
    if (name === 'password' && mode === 'signup') {
      setPasswordValidation(validatePasswordStrength(value));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const endpoint = mode === 'login' ? '/api/auth/login' : '/api/auth/register';
      const payload = mode === 'login'
        ? { email: formData.email, password: formData.password }
        : formData;

      const response = await fetch(getApiUrl(endpoint), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Send cookies with request
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || 'Authentication failed');
      }

      // Store CSRF token from login/register response
      if (data.csrfToken) {
        setCsrfToken(data.csrfToken);
      }

      // Token is now in httpOnly cookie (no localStorage needed)
      // Store only non-sensitive user data in localStorage for UI purposes
      localStorage.setItem('user', JSON.stringify(data.user));

      // Call success callback
      if (onAuthSuccess) {
        onAuthSuccess(data.user);
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setError('');
    setResetEmailSent(false);
    setFormData({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      phone: ''
    });
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(getApiUrl('/api/auth/forgot-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Send cookies with request
        body: JSON.stringify({ email: formData.email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || 'Failed to send reset email');
      }

      setResetEmailSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <motion.div 
        className="auth-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="auth-header">
          <div className="auth-header-logo">
            <div className="jpcc-seal-container">
              <img
                src={`${process.env.PUBLIC_URL}/JPCC_seal_trans_white.png`}
                alt="Jefferson Parish Clerk of Court Seal"
                className="jpcc-seal auth-seal"
              />
            </div>
            <div className="auth-header-text">
              <h1 className="auth-title">Jon A. Gegenheimer</h1>
              <p className="auth-subtitle">Jefferson Parish Clerk of Court</p>
              <p className="auth-subtitle auth-subtitle-secondary">Marriage License Pre-Application</p>
            </div>
          </div>
        </div>

        {mode !== 'forgot-password' && (
          <div className="auth-tabs">
            <button
              className={`tab ${mode === 'login' ? 'active' : ''}`}
              onClick={() => switchMode('login')}
            >
              <span>Log In</span>
              {mode === 'login' && (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              )}
            </button>
            <button
              className={`tab ${mode === 'signup' ? 'active' : ''}`}
              onClick={() => switchMode('signup')}
            >
              <span>Sign Up</span>
              {mode === 'signup' && (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              )}
            </button>
          </div>
        )}

        {mode === 'forgot-password' ? (
          <form onSubmit={handleForgotPassword} className="auth-form">
            <AnimatePresence mode="wait">
              {error && (
                <motion.div
                  className="error-message"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            {resetEmailSent ? (
              <motion.div
                className="success-message"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <p>
                  <strong>Check your email!</strong>
                </p>
                <p>
                  We've sent a password reset link to <strong>{formData.email}</strong>.
                  Please check your inbox and follow the instructions to reset your password.
                </p>
              </motion.div>
            ) : (
              <>
                <div className="forgot-password-header">
                  <h3>Reset Your Password</h3>
                  <p>Enter your email address and we'll send you a link to reset your password.</p>
                </div>

                <div className="input-group">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Email"
                    autoComplete="email"
                    required
                    className="form-input"
                  />
                </div>

                <motion.button
                  type="submit"
                  className="submit-button"
                  disabled={loading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {loading ? (
                    <div className="loader"></div>
                  ) : (
                    <>
                      <span>Send Reset Link</span>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </>
                  )}
                </motion.button>
              </>
            )}

            <div className="forgot-password-footer">
              <button
                type="button"
                className="link-button"
                onClick={() => switchMode('login')}
              >
                Back to Login
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleSubmit} className="auth-form">
          <AnimatePresence mode="wait">
            {error && (
              <motion.div
                className="error-message"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                {error}
              </motion.div>
            )}
          </AnimatePresence>

          {/* OAuth buttons section */}
          {process.env.REACT_APP_GOOGLE_OAUTH_ENABLED !== 'false' && (
            <>
              <div className="social-buttons">
                <GoogleSignInButton mode={mode === 'login' ? 'signin_with' : 'signup_with'} />
              </div>
              <div className="divider"><span>or continue with email</span></div>
            </>
          )}

          <AnimatePresence mode="wait">
            {mode === 'signup' && (
              <motion.div
                key="signup-fields"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="signup-fields"
              >
                <div className="form-row">
                  <div className="input-group">
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      placeholder="First Name"
                      autoComplete="given-name"
                      className="form-input"
                    />
                  </div>
                  <div className="input-group">
                    <input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      placeholder="Last Name"
                      autoComplete="family-name"
                      className="form-input"
                    />
                  </div>
                </div>
                <div className="input-group">
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Phone Number (optional)"
                    autoComplete="tel"
                    className="form-input"
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="input-group">
            <div className="input-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
              </svg>
            </div>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Email"
              autoComplete="username"
              required
              className="form-input with-icon"
            />
          </div>

          <div className="input-group">
            <div className="input-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder={mode === 'signup' ? 'Create a Password' : 'Password'}
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              required
              className="form-input with-icon"
            />
          </div>

          {mode === 'signup' && (
            <motion.div
              className="password-requirements"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              <p className="requirements-title">Password must contain:</p>
              <ul className="requirements-list">
                <li className={passwordValidation.length ? 'valid' : 'invalid'}>
                  {passwordValidation.length ? 'Met' : 'Needed'}: At least 12 characters
                </li>
                <li className={passwordValidation.uppercase ? 'valid' : 'invalid'}>
                  {passwordValidation.uppercase ? 'Met' : 'Needed'}: One uppercase letter (A-Z)
                </li>
                <li className={passwordValidation.lowercase ? 'valid' : 'invalid'}>
                  {passwordValidation.lowercase ? 'Met' : 'Needed'}: One lowercase letter (a-z)
                </li>
                <li className={passwordValidation.number ? 'valid' : 'invalid'}>
                  {passwordValidation.number ? 'Met' : 'Needed'}: One number (0-9)
                </li>
                <li className={passwordValidation.special ? 'valid' : 'invalid'}>
                  {passwordValidation.special ? 'Met' : 'Needed'}: One special character (!@#$%^&*)
                </li>
              </ul>
            </motion.div>
          )}

          {mode === 'signup' && (
            <p className="account-trust-copy">We use your account to save your progress and protect your application. By creating an account, you acknowledge that submitted information is handled as a Clerk of Court record. Contact the Marriage License office with records or privacy questions.</p>
          )}

          {mode === 'login' && (
            <div className="forgot-password">
              <button
                type="button"
                className="link-button"
                onClick={() => switchMode('forgot-password')}
              >
                Don't remember your password?
              </button>
            </div>
          )}

          <motion.button
            type="submit"
            className="submit-button"
            disabled={loading}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {loading ? (
              <div className="loader"></div>
            ) : (
              <>
                <span>{mode === 'login' ? 'Log In' : 'Create Account'}</span>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </>
            )}
          </motion.button>
        </form>
        )}
      </motion.div>
    </div>
  );
};

export default Auth;
